import { Step, StepLabel, Stepper } from '@mui/material';
import { type ReactNode, useMemo } from 'react';
import { z, type ZodType } from 'zod';
import { ErrorBoundary } from '~/atoms';

import {
  DataImportWizardActions,
  DataImportWizardFileStep,
  DataImportWizardMappingStep,
  DataImportWizardPreviewStep,
  type DataImportWizardPreviewRenderContext,
} from './components/wizard/DataImportWizardSteps';
import { type DataImportConfigInput } from './dataImport.config';
import { useDataImportCopy } from './dataImport.copy';
import { type DataImportPreviewRemoteIssue } from './dataImport.preview';
import { getDataImportConfirmDecision } from './dataImportWizard.state';
import {
  type DataImportWizardStepId,
  type DataImportWizardSteps,
  getDataImportWizardStepIndex,
} from './dataImportWizard.steps';
import {
  type DataImportWizardInput,
  type DataImportWizardValidationInput,
  useDataImportWizard,
} from './useDataImportWizard';

type DataImportWizardProps<
  TPreview,
  TResult,
  TValidationSchema extends ZodType = ZodType,
> = {
  readonly config: DataImportConfigInput<TValidationSchema, TPreview>;
  readonly getRemoteIssues?: (
    previewResult: TPreview,
  ) =>
    | Promise<readonly DataImportPreviewRemoteIssue[]>
    | readonly DataImportPreviewRemoteIssue[];
  readonly onCancel: () => void;
  readonly onImport: (
    input: DataImportWizardInput<z.output<TValidationSchema>>,
  ) => Promise<TResult> | TResult;
  readonly onValidate: (
    input: DataImportWizardValidationInput<z.output<TValidationSchema>>,
  ) => Promise<TPreview> | TPreview;
  readonly renderPreview?: (
    context: DataImportWizardPreviewRenderContext<TPreview>,
  ) => ReactNode;
  readonly renderResult: (result: TResult) => ReactNode;
  readonly renderSubmitError?: (error: Error) => ReactNode;
  /** Defaults to file → mapping → preview. */
  readonly steps?: DataImportWizardSteps;
};

/**
 * Invokes `renderResult` from its own render, so a surrounding
 * `ErrorBoundary` catches a custom renderer that throws.
 */
function ResultRenderer<TResult>({
  renderResult,
  result,
}: {
  readonly renderResult: (result: TResult) => ReactNode;
  readonly result: TResult;
}) {
  return <>{renderResult(result)}</>;
}

function getStepLabel(
  step: DataImportWizardStepId,
  copy: { stepMapColumns: string; stepPreview: string; stepSelectFile: string },
) {
  switch (step) {
    case 'file':
      return copy.stepSelectFile;
    case 'mapping':
      return copy.stepMapColumns;
    case 'preview':
      return copy.stepPreview;
  }
}

export function DataImportWizard<
  TPreview,
  TResult,
  TValidationSchema extends ZodType = ZodType,
>({
  config,
  getRemoteIssues,
  onCancel,
  onImport,
  onValidate,
  renderPreview,
  renderResult,
  renderSubmitError,
  steps,
}: DataImportWizardProps<TPreview, TResult, TValidationSchema>) {
  const {
    actions,
    hasMappingStep,
    resolvedConfig,
    state,
    steps: wizardSteps,
  } = useDataImportWizard({
    config,
    getRemoteIssues,
    onImport,
    onValidate,
    steps,
  });
  const { file: fileConfig, renderAdditionalForm } = resolvedConfig;
  const copy = useDataImportCopy(resolvedConfig.copy);

  const stepLabels = useMemo(
    () => wizardSteps.map((step) => getStepLabel(step, copy)),
    [copy, wizardSteps],
  );
  const activeStepIndex = getDataImportWizardStepIndex(
    wizardSteps,
    state.activeStep,
  );

  const canContinueFromFile =
    Boolean(state.parsedFile) &&
    (!renderAdditionalForm || state.formState.isValid);
  const confirmDecision = getDataImportConfirmDecision(
    state,
    resolvedConfig.canConfirm,
  );

  return (
    <div className="flex h-full min-h-0 w-full flex-col gap-6">
      <Stepper activeStep={activeStepIndex} className="shrink-0">
        {stepLabels.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {state.activeStep === 'file' ? (
        <DataImportWizardFileStep
          config={resolvedConfig}
          copy={copy}
          file={state.file}
          formData={state.formState.data}
          isParsing={state.isParsing}
          parseError={state.parseError}
          onFormChange={actions.updateFormState}
          onRemoveFile={actions.removeFile}
          onSelectFile={actions.selectFileForImport}
        />
      ) : null}

      {state.activeStep === 'mapping' && state.parsedFile ? (
        <DataImportWizardMappingStep
          columnMapping={state.columnMapping}
          copy={copy}
          fileConfig={fileConfig}
          parsedFile={state.parsedFile}
          onColumnMappingChange={actions.updateColumnMapping}
        />
      ) : null}

      {state.activeStep === 'preview' ? (
        <DataImportWizardPreviewStep
          copy={copy}
          importError={state.importError}
          isImporting={state.isImporting}
          isValidating={state.isValidating}
          preview={state.preview}
          previewError={state.previewError}
          previewModel={state.previewModel}
          renderPreview={renderPreview}
          renderSubmitError={renderSubmitError}
        />
      ) : null}

      {state.activeStep === 'result' && state.result.status === 'ready' ? (
        <ErrorBoundary fallbackMessage={copy.resultFallback}>
          <ResultRenderer
            renderResult={renderResult}
            result={state.result.value}
          />
        </ErrorBoundary>
      ) : null}

      <DataImportWizardActions
        activeStep={state.activeStep}
        canContinueFromFile={canContinueFromFile}
        confirmBlockedReason={
          confirmDecision.allowed ? undefined : confirmDecision.reason
        }
        copy={copy}
        hasMappingStep={hasMappingStep}
        isColumnMappingValid={state.isColumnMappingValid}
        isImporting={state.isImporting}
        isValidating={state.isValidating}
        previewReady={confirmDecision.allowed}
        onBack={actions.goBack}
        onCancel={onCancel}
        onConfirm={actions.importFile}
        onNextFromFile={
          hasMappingStep ? actions.goToMapping : actions.validatePreview
        }
        onNextFromMapping={actions.validatePreview}
      />
    </div>
  );
}

export type { DataImportWizardProps };
export type { DataImportWizardPreviewRenderContext } from './components/wizard/DataImportWizardSteps';
export type {
  DataImportWizardStepId,
  DataImportWizardSteps,
} from './dataImportWizard.steps';
export type {
  DataImportWizardInput,
  DataImportWizardValidationInput,
} from './useDataImportWizard';
